// src/components/maintenance/MaintenanceGate.jsx
//
// Wraps the whole app. On load it asks the backend whether THIS SURFACE is in
// maintenance and renders accordingly. The backend is the single source of
// truth — there is no build-time flag anymore.
//
// Maintenance v3 is per-surface: we send `?surface=web`, and the server returns
// the flag already resolved as `global.enabled || web.enabled`. So a website
// deploy no longer gates the Android app, and an API outage still gates both.
// The public read carries no `surfaces` block — that is admin-only now — so
// this gate reads exactly two fields, `maintenance` and `message`.
//
//   status:false                    -> full site (everyone)
//   status:true  + valid token      -> full site (admin working behind scenes)
//   status:true  + no/invalid token -> maintenance page, re-polls every 10s
//   backend unreachable / 5xx       -> "unreachable" screen, re-polls every 5s
//
// When an admin is unlocked, a floating AdminMaintenanceToggle is shown so the
// admin can flip the public maintenance flag via PUT /api/maintenance.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_ENDPOINTS, CLIENT_SURFACE } from "../../config/apiConfig";
import { getAdminToken, clearAdminToken, authHeaders } from "./adminAuth";
import MaintenancePage from "./MaintenancePage";
import MaintenanceUnreachable from "./MaintenanceUnreachable";
import AdminMaintenanceToggle from "./AdminMaintenanceToggle";
import "./MaintenancePage.css";

const S = {
  LOADING: "loading",
  LIVE: "live",
  MAINTENANCE: "maintenance",
  ADMIN: "admin",
  UNREACHABLE: "unreachable",
};

// How often to re-check, per state. Only the two states that hold the user out
// poll at all — once the site is LIVE there is nothing to wait for, and an
// unlocked ADMIN is already seeing the site.
//
//   MAINTENANCE — 10s, so the site lets itself back in without a refresh
//   UNREACHABLE — 5s, to recover quickly from a blip
// Build brief §16: the backend keeps `global` maintenance on deliberately while
// the API serves normally, and this gate honours the flag as a blocking screen.
// A developer build can ignore it with REACT_APP_IGNORE_MAINTENANCE=1 in
// .env.development.local. It is a build-time value, it logs loudly, and it
// must never be set for a production build.
const IGNORE_FLAG = process.env.REACT_APP_IGNORE_MAINTENANCE === "1";

const POLL_MS = {
  [S.MAINTENANCE]: 10000,
  [S.UNREACHABLE]: 5000,
};

export default function MaintenanceGate({ children }) {
  const [state, setState] = useState(S.LOADING);
  const [message, setMessage] = useState("Genius at work..");
  // Tracks the backend's current maintenance flag, so the admin toggle knows
  // which way to flip even when the admin is viewing the full (LIVE/ADMIN) site.
  const [maintenanceFlag, setMaintenanceFlag] = useState(true);
  // Whether this browser holds a valid admin token (drives toggle visibility).
  const [isAdmin, setIsAdmin] = useState(false);
  const retryRef = useRef(null);

  const evaluate = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.MAINTENANCE_STATUS(CLIENT_SURFACE), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        // The server sends no-store; this stops the browser's own HTTP cache
        // pinning a stale answer on a gate that re-polls unattended.
        cache: "no-store",
      });

      if (!res.ok) {
        setState(S.UNREACHABLE);
        return;
      }

      const data = await res.json();

      // A surface id the backend doesn't know resolves against `global` and
      // still returns 200 — it never 4xxs, so a typo can't lock anyone out.
      // It also gates nothing of ours, which is the part worth shouting about.
      if (data.unknown_surface) {
        console.warn(
          `[maintenance] backend does not recognise surface "${CLIENT_SURFACE}" — ` +
          `resolving against global only. Web-specific maintenance will not gate this build.`
        );
      }

      // Gate only on an explicit `true`. Anything else — missing, null, a
      // string, a reshaped payload — means the site stays up. Per the v3
      // contract, a response-shape change may degrade the message but must
      // never lock users out. Unreachable is handled separately, above.
      let gated = data.maintenance === true;
      if (gated && IGNORE_FLAG) {
        console.warn("[maintenance] flag is ON but REACT_APP_IGNORE_MAINTENANCE=1 — ignoring it in this developer build.");
        gated = false;
      }
      if (typeof data.maintenance !== "boolean") {
        console.warn("[maintenance] `maintenance` was not a boolean; treating the site as live.", data);
      }

      if (data.message) setMessage(data.message);
      setMaintenanceFlag(gated);

      // Check admin token regardless of maintenance flag, so the toggle shows
      // even when the site is already live.
      const token = getAdminToken();
      let adminOk = false;
      if (token) {
        try {
          const vres = await fetch(API_ENDPOINTS.ADMIN_VERIFY, {
            method: "GET",
            headers: { "Content-Type": "application/json", ...authHeaders() },
          });
          if (vres.ok) {
            adminOk = true;
          } else {
            clearAdminToken();
          }
        } catch {
          // verify failed at network level -> unreachable, don't leak site
          setState(S.UNREACHABLE);
          return;
        }
      }
      setIsAdmin(adminOk);

      if (!gated) {
        setState(S.LIVE);
      } else if (adminOk) {
        setState(S.ADMIN);
      } else {
        setState(S.MAINTENANCE);
      }
    } catch {
      setState(S.UNREACHABLE);
    }
  }, []);

  useEffect(() => {
    evaluate();
  }, [evaluate]);

  useEffect(() => {
    const every = POLL_MS[state];
    if (!every) return undefined;
    retryRef.current = setInterval(evaluate, every);
    return () => clearInterval(retryRef.current);
  }, [state, evaluate]);

  // Maintenance page login success.
  const handleUnlock = useCallback(() => {
    setIsAdmin(true);
    setState(S.ADMIN);
  }, []);

  // Admin flipped the flag from the toggle.
  const handleFlagChanged = useCallback((newVal) => {
    setMaintenanceFlag(newVal);
    // Admin stays on the full site either way; just keep flag in sync.
    setState(newVal ? S.ADMIN : S.LIVE);
  }, []);

  // Admin logged out from the toggle.
  const handleAdminLogout = useCallback(() => {
    clearAdminToken();
    setIsAdmin(false);
    // If the public site is live, stay live; otherwise show maintenance.
    setState(maintenanceFlag ? S.MAINTENANCE : S.LIVE);
  }, [maintenanceFlag]);

  // The floating toggle, shown whenever this browser is an unlocked admin.
  const adminToggle = isAdmin ? (
    <AdminMaintenanceToggle
      initialMaintenance={maintenanceFlag}
      onChanged={handleFlagChanged}
      onLogout={handleAdminLogout}
    />
  ) : null;

  switch (state) {
    case S.LOADING:
      return <div className="maint-loading" />;
    case S.LIVE:
    case S.ADMIN:
      return (
        <>
          {children}
          {adminToggle}
        </>
      );
    case S.UNREACHABLE:
      return <MaintenanceUnreachable />;
    case S.MAINTENANCE:
    default:
      return <MaintenancePage message={message} onUnlock={handleUnlock} />;
  }
}
